import axios from '@/lib/axios'

export interface Ticket {
  _id: string
  ticketNumber: number
  subject: string
  description: string
  status: 'open' | 'in_progress' | 'waiting_customer' | 'resolved' | 'closed'
  priority: 'low' | 'medium' | 'high' | 'urgent'
  category: 'bug' | 'feature_request' | 'question' | 'billing' | 'technical' | 'other'
  createdBy: {
    userId: string
    userType: string
    name: string
    email: string
  }
  company: {
    _id: string
    name: string
  }
  assignedTo?: {
    _id: string
    name: string
    email: string
  }
  attachments: Array<{
    filename: string
    originalName: string
    url: string
    mimeType: string
    size: number
    uploadedAt: string
  }>
  customerUnread: boolean
  adminUnread: boolean
  lastMessageAt: string
  lastMessagePreview: string
  resolvedAt?: string
  closedAt?: string
  createdAt: string
  updatedAt: string
}

export interface TicketMessage {
  _id: string
  ticket: string
  content: string
  sender: {
    userId: string
    userType: 'customer' | 'admin'
    name: string
    email: string
  }
  attachments: Array<{
    filename: string
    originalName: string
    url: string
    mimeType: string
    size: number
  }>
  isSystemMessage: boolean
  emailSent: boolean
  emailSentAt?: string
  createdAt: string
}

export interface TicketStats {
  byStatus: Array<{ _id: string; count: number }>
  totalUnread: number
  todayNew: number
}

export const ticketService = {
  // Tüm ticketları getir
  async getTickets(params?: {
    status?: string
    priority?: string
    category?: string
    company?: string
    page?: number
    limit?: number
  }) {
    const response = await axios.get('/tickets', { params })
    return response.data
  },

  // Ticket detayını getir
  async getTicketById(id: string): Promise<{ ticket: Ticket; messages: TicketMessage[] }> {
    const response = await axios.get(`/tickets/${id}`)
    return response.data.data
  },

  // Ticketa mesaj ekle
  async addMessage(ticketId: string, content: string, attachments?: any[]) {
    const response = await axios.post(`/tickets/${ticketId}/messages`, {
      content,
      attachments
    })
    return response.data.data
  },

  // Ticket durumunu güncelle
  async updateStatus(ticketId: string, status: string) {
    const response = await axios.patch(`/tickets/${ticketId}/status`, { status })
    return response.data.data
  },

  // Ticket'ı ata
  async assignTicket(ticketId: string, adminId: string) {
    const response = await axios.patch(`/tickets/${ticketId}/assign`, { adminId })
    return response.data.data
  },

  // Okunmamış sayısını getir
  async getUnreadCount(): Promise<number> {
    const response = await axios.get('/tickets/admin/unread-count')
    return response.data.data?.unreadCount || 0
  },

  // İstatistikleri getir
  async getStats(): Promise<TicketStats> {
    const response = await axios.get('/tickets/admin/stats')
    return response.data.data
  },

  // Ticket sil
  async deleteTicket(ticketId: string): Promise<void> {
    await axios.delete(`/tickets/${ticketId}`)
  },

  // Helper fonksiyonlar
  getStatusText(status: string): string {
    const map: Record<string, string> = {
      'open': 'Açık',
      'in_progress': 'İşlemde',
      'waiting_customer': 'Müşteri Bekleniyor',
      'resolved': 'Çözüldü',
      'closed': 'Kapatıldı'
    }
    return map[status] || status
  },

  getStatusColor(status: string): string {
    const map: Record<string, string> = {
      'open': 'bg-green-100 text-green-800',
      'in_progress': 'bg-blue-100 text-blue-800',
      'waiting_customer': 'bg-yellow-100 text-yellow-800',
      'resolved': 'bg-purple-100 text-purple-800',
      'closed': 'bg-gray-100 text-gray-800'
    }
    return map[status] || 'bg-gray-100 text-gray-800'
  },

  getPriorityText(priority: string): string {
    const map: Record<string, string> = {
      'low': 'Düşük',
      'medium': 'Orta',
      'high': 'Yüksek',
      'urgent': 'Acil'
    }
    return map[priority] || priority
  },

  getPriorityColor(priority: string): string {
    const map: Record<string, string> = {
      'low': 'bg-gray-100 text-gray-700',
      'medium': 'bg-blue-100 text-blue-700',
      'high': 'bg-orange-100 text-orange-700',
      'urgent': 'bg-red-100 text-red-700'
    }
    return map[priority] || 'bg-gray-100 text-gray-700'
  },

  getCategoryText(category: string): string {
    const map: Record<string, string> = {
      'bug': 'Hata Bildirimi',
      'feature_request': 'Özellik İsteği',
      'question': 'Soru',
      'billing': 'Faturalama',
      'technical': 'Teknik Destek',
      'other': 'Diğer'
    }
    return map[category] || category
  }
}
